import React, { useState, useRef, useEffect } from 'react';
import AppImage from './ui/AppImage';
import Icon from './ui/AppIcon';
import './FeaturedProduct2.css';
import { useFeaturedLaptops } from '../hooks/useFirestore';
import { useCart } from '../context/CartContext';
import { soundEffects } from '../utils/soundEffects';
import { generateProductSlug } from '../utils/seoUtils';
import { useNavigate } from 'react-router-dom';
import { useEnhancedAddToCart } from '../hooks/useEnhancedAddToCart';

function ProductCard({ laptop, onAddToCart }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const cardRef = useRef(null);
  const navigate = useNavigate();

  const images = laptop.imageUrls && laptop.imageUrls.length > 0 ? laptop.imageUrls : [laptop.image || 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=800&q=80'];
  const currentImage = images[selectedImageIdx];

  const handleAdd = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(laptop, e.currentTarget);
    }
  };

  const handleNavigate = (e) => {
    e.stopPropagation();
    const slug = generateProductSlug(laptop);
    navigate(`/product/${slug || laptop.id}`);
  };

  const specifications = [
    laptop.processor,
    laptop.ramSize,
    laptop.storageSize ? `${laptop.storageSize} ${laptop.storageType || 'SSD'}` : null
  ].filter(Boolean);

  return (
    <div
      ref={cardRef}
      className={`product-card rounded-4xl overflow-hidden cursor-pointer shrink-0 w-72 md:w-80 transition-all duration-500 ${expanded ? 'ring-2 ring-plum/20' : ''}`}
      onClick={() => setExpanded(!expanded)}>

      {/* Product image */}
      <div className="relative w-full h-56 overflow-hidden" onClick={handleNavigate}>
        <AppImage
          src={currentImage}
          alt={`${laptop.brand} — ${laptop.name}`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          style={{ filter: 'brightness(1.04) contrast(1.02)' }}
        />

        {/* Subtle Bottom shadow for badge contrast, removed whitening top overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none" />
        {/* Expand indicator - now Primary Blue */}
        <div
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-primary-blue-gradient flex items-center justify-center"
          onClick={(e) => { e.stopPropagation(); setExpanded((s) => !s); }}
          role="button"
          tabIndex={0}
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
        >
          <Icon
            name={expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            size={12}
            className="text-white" />
        </div>
        {/* Price badge - now Primary Blue */}
        <div className="absolute bottom-3 left-3 bg-primary-blue-gradient px-3 py-1 rounded-full">
          <span className="text-xs font-black text-white">₹{laptop.price?.toLocaleString() || '0'}</span>
        </div>
      </div>

      {/* Card body */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-plum leading-tight mb-0.5" onClick={handleNavigate}>{laptop.brand || 'Unknown Brand'}</h3>
        <p className="text-[11px] text-plum/50 font-medium mt-0.5 italic">{laptop.name || 'Unknown Product'}</p>

        {/* Shade swatches - now Image Swatches */}
        <div className="flex gap-1.5 mt-3">
          {images.slice(0, 4).map((img, idx) =>
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setSelectedImageIdx(idx); }}
              className={`swatch-pulse w-6 h-6 rounded-full border-2 transition-all overflow-hidden ${selectedImageIdx === idx ? 'border-plum scale-110' : 'border-white/60'}`}
              title={laptop.processor}
              aria-label={`Select view ${idx + 1}`}>
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          )}
        </div>

        {/* Selected shade name - now Selected View/Spec */}
        <p className="text-[10px] text-plum/40 mt-1.5 font-medium mb-0">
          {laptop.processor || 'Core Performance'}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={handleAdd}
            className="btn-shimmer flex-1 bg-plum text-white py-2.5 rounded-full text-[10px] font-semibold tracking-wide hover:bg-plum-light transition-colors">
            Add to Bag
          </button>
          <button
            onClick={handleNavigate}
            className="flex-1 border border-plum text-plum py-2.5 rounded-full text-[10px] font-semibold tracking-wide hover:bg-plum/5 transition-colors">
            Details
          </button>
        </div>
      </div>

      {/* Expandable detail panel */}
      <div className={`detail-panel ${expanded ? 'open' : ''}`}>
        <div className="px-5 pb-5 space-y-4 border-t border-plum-100/20 pt-4">
          {/* Ingredients - now Specifications */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-plum/40 font-semibold mb-2">Key Specifications</p>
            <div className="flex flex-wrap gap-1.5">
              {specifications.map((spec, idx) =>
                <span key={idx} className="text-[10px] bg-lilac/40 text-plum/70 px-2.5 py-1 rounded-full font-medium">
                  {spec}
                </span>
              )}
            </div>
          </div>

          {/* Best for - now Includes */}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-plum/40 font-semibold mb-1">Includes</p>
            <p className="text-xs text-plum/60">includes adapter, 6 month warranty</p>
          </div>

          {/* Video thumb - now 2nd Image */}
          <div className="relative rounded-2xl overflow-hidden h-28 bg-gray-100" onClick={handleNavigate}>
            <AppImage
              src={images[1] || currentImage}
              alt={`Application view for ${laptop.name}`}
              className="w-full h-full object-cover" />

            <div className="absolute inset-0 bg-plum/30 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center">
                <Icon name="PlayIcon" size={16} className="text-plum ml-0.5" />
              </div>
            </div>
            <div className="absolute bottom-2 left-2 text-[9px] text-white/80 font-medium uppercase tracking-wider">
              Experience the performance
            </div>
          </div>
        </div>
      </div>
    </div>);
}

export default function FeaturedProduct2() {
  const sectionRef = useRef(null);
  const { data: featuredLaptops, loading, error } = useFeaturedLaptops();
  const { addToCart } = useCart();
  // eslint-disable-next-line no-unused-vars
  const [addedToCartEffect, setAddedToCartEffect] = useState(null);
  const { handleEnhancedAddToCart } = useEnhancedAddToCart();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    const reveals = sectionRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [featuredLaptops]);

  const handleAddToCart = (laptop, buttonElement) => {
    handleEnhancedAddToCart(laptop, null, null, buttonElement);
    
    setAddedToCartEffect(laptop.id);
    setTimeout(() => setAddedToCartEffect(null), 2000);
  };

  const splitWords = (text, startDelay = 0) => {
    const words = text.trim().split(/\s+/);
    return words.map((word, i) => (
      <span
        key={`${word}-${i}`}
        className="word-animate"
        style={{ animationDelay: `${startDelay + i * 40}ms` }}
      >
        {word}
        {i < words.length - 1 ? ' ' : ''}
      </span>
    ));
  };

  if (loading && featuredLaptops.length === 0) {
    return null; // Or a skeleton loader
  }

  return (
    <div className="featured-product-2 overflow-hidden relative" style={{ '--bg-image': "url('/images/featuredimage/2149529358.jpg')" }}>
      <section
        id="collections"
        ref={sectionRef}
        className="bg-clinical-white py-16 px-0">

        <div className="max-w-7xl mx-auto mb-16 px-6">
          <div className="section-fade reveal">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-6 h-[1px] bg-website-blue" style={{ backgroundColor: 'var(--website-primary)' }} />
              <span className="font-mono-label text-[9px] font-bold text-website-blue uppercase tracking-[0.5em]" style={{ color: 'var(--website-primary)' }}>
                Featured Collections
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
              {splitWords('Handpicked devices with', 100)}{' '}
              <span className="text-website-blue inline-block" style={{ color: 'var(--website-primary)' }}>
                {splitWords('performance.', 240)}
              </span>
            </h2>
          </div>
        </div>

        <div className="mb-20">
          {/* Horizontal scroll row */}
          <div className="px-6 md:px-12 overflow-x-auto no-scrollbar">
            <div className="flex gap-5 pb-4" style={{ width: 'max-content' }}>
              {featuredLaptops.slice(0, 3).map((laptop) =>
                <ProductCard key={laptop.id} laptop={laptop} onAddToCart={handleAddToCart} />
              )}

              {/* View all card */}
              <div className="shrink-0 w-40 flex items-center justify-center">
                <a href="/products" className="flex flex-col items-center gap-3 text-website-blue/30 hover:text-website-blue group no-underline">
                  <div className="w-12 h-12 rounded-full border-2 flex items-center justify-center">
                    <Icon name="ArrowRightIcon" size={16} className="text-current" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-semibold">View All</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
